import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { getAllSurveys } from '../../config/surveys';
import { CHART_COLORS, useAdminContext } from './shared';
import type { RespondentType } from './shared';
import './AdminPages.css';

interface VersionInfo {
  version: string;
  surveyId: string;
  responseCount: number;
}

interface AnswerData {
  value: string;
  count: number;
  percentage: number;
}

interface QuestionComparison {
  questionId: string;
  questionText: string | null;
  versions: Record<string, {
    totalResponses: number;
    answers: AnswerData[];
  }>;
}

interface ComparisonData {
  surveyId: string;
  versions: string[];
  data: QuestionComparison[];
}

const SURVEY_OPTIONS_BY_ROLE: Record<RespondentType, { value: string; label: string }[]> = {
  student: [
    { value: 'k-2', label: 'K-2 Survey' },
    { value: '3-5', label: 'Grades 3-5 Survey' },
    { value: '6-8', label: 'Grades 6-8 Survey' },
    { value: 'high-school', label: 'High School Survey' },
    { value: 'virtual-k8', label: 'Virtual K-8 Survey' },
    { value: 'virtual-hs', label: 'Virtual High School Survey' },
  ],
  staff: [
    { value: 'staff-climate', label: 'Staff Climate Survey' },
  ],
  parent: [],
};

const ComparisonPage: React.FC = () => {
  const { token } = useAuth();
  const { respondentType } = useAdminContext();
  const [availableVersions, setAvailableVersions] = useState<VersionInfo[]>([]);
  const [surveyId, setSurveyId] = useState<string>('');
  const [version1, setVersion1] = useState<string>('');
  const [version2, setVersion2] = useState<string>('');
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build question text lookup from survey configs
  const questionTextMap = useMemo(() => {
    const map = new Map<string, string>();
    const surveys = getAllSurveys();
    for (const survey of surveys) {
      for (const question of survey.questions) {
        map.set(question.id, question.text);
      }
    }
    return map;
  }, []);

  const surveyOptions = SURVEY_OPTIONS_BY_ROLE[respondentType] || SURVEY_OPTIONS_BY_ROLE.student;

  // Fetch available versions
  useEffect(() => {
    const fetchVersions = async () => {
      if (!token) return;

      try {
        const response = await fetch(`/api/admin/stats/versions?role=${respondentType}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setAvailableVersions(data.versions);

          // Set default survey ID if available
          if (data.versions.length > 0) {
            const firstSurveyId = data.versions[0].surveyId;
            setSurveyId(firstSurveyId);
          } else {
            // Default to first option for this role
            const opts = SURVEY_OPTIONS_BY_ROLE[respondentType] || [];
            setSurveyId(opts.length > 0 ? opts[0].value : '');
          }
        }
      } catch (err) {
        console.error('Failed to fetch versions:', err);
      }
    };

    fetchVersions();
  }, [token, respondentType]);

  // Get versions for selected survey
  const versionsForSurvey = useMemo(() => {
    return availableVersions
      .filter((v) => v.surveyId === surveyId)
      .sort((a, b) => b.version.localeCompare(a.version));
  }, [availableVersions, surveyId]);

  // Auto-select versions when survey changes
  useEffect(() => {
    if (versionsForSurvey.length >= 2) {
      setVersion1(versionsForSurvey[0].version);
      setVersion2(versionsForSurvey[1].version);
    } else if (versionsForSurvey.length === 1) {
      setVersion1(versionsForSurvey[0].version);
      setVersion2('');
    } else {
      setVersion1('');
      setVersion2('');
    }
  }, [versionsForSurvey]);

  // Fetch comparison data
  useEffect(() => {
    const fetchComparison = async () => {
      if (!token || !surveyId || !version1 || !version2) {
        setComparisonData(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/admin/stats/compare?surveyType=${surveyId}&versions[]=${version1}&versions[]=${version2}&role=${respondentType}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch comparison data');
        }

        const data = await response.json();
        setComparisonData(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchComparison();
  }, [token, surveyId, version1, version2]);

  // Get question text from our local map
  const getQuestionText = (questionId: string): string => {
    return questionTextMap.get(questionId) || questionId;
  };

  // Format answer value for display
  const formatAnswer = (value: string): string => {
    return value
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Prepare chart data for a question
  const prepareChartData = (question: QuestionComparison) => {
    const allAnswers = new Set<string>();
    Object.values(question.versions).forEach((v) => {
      v.answers.forEach((a) => allAnswers.add(a.value));
    });

    return Array.from(allAnswers).map((answer) => {
      const dataPoint: Record<string, any> = { name: formatAnswer(answer) };

      if (version1 && question.versions[version1]) {
        const answerData = question.versions[version1].answers.find((a) => a.value === answer);
        dataPoint[`v${version1}`] = answerData?.percentage || 0;
      }

      if (version2 && question.versions[version2]) {
        const answerData = question.versions[version2].answers.find((a) => a.value === answer);
        dataPoint[`v${version2}`] = answerData?.percentage || 0;
      }

      return dataPoint;
    });
  };

  // Calculate delta for a question
  const calculateDelta = (question: QuestionComparison) => {
    const v1Data = question.versions[version1];
    const v2Data = question.versions[version2];

    if (!v1Data || !v2Data) return null;

    // Calculate positive response change (for emoji-scale questions)
    const positiveAnswers = ['always', 'most-of-the-time'];
    let v1Positive = 0;
    let v2Positive = 0;

    v1Data.answers.forEach((a) => {
      if (positiveAnswers.includes(a.value)) v1Positive += a.percentage;
    });

    v2Data.answers.forEach((a) => {
      if (positiveAnswers.includes(a.value)) v2Positive += a.percentage;
    });

    return v1Positive - v2Positive; // Positive = v1 is better
  };

  return (
    <div className="admin-page comparison-page">
      <div className="page-header">
        <div>
          <h2>Version Comparison</h2>
          <p className="page-subtitle">Compare survey results between different versions</p>
        </div>
      </div>

      <div className="cp-selectors">
        <div className="cp-selector">
          <label>Survey Type</label>
          <select
            value={surveyId}
            onChange={(e) => setSurveyId(e.target.value)}
          >
            {surveyOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="cp-selector">
          <label>Version 1 (Newer)</label>
          <select
            value={version1}
            onChange={(e) => setVersion1(e.target.value)}
            disabled={versionsForSurvey.length === 0}
          >
            <option value="">Select version...</option>
            {versionsForSurvey.map((v) => (
              <option key={v.version} value={v.version}>
                {v.version} ({v.responseCount} responses)
              </option>
            ))}
          </select>
        </div>
        <div className="cp-selector">
          <label>Version 2 (Older)</label>
          <select
            value={version2}
            onChange={(e) => setVersion2(e.target.value)}
            disabled={versionsForSurvey.length < 2}
          >
            <option value="">Select version...</option>
            {versionsForSurvey
              .filter((v) => v.version !== version1)
              .map((v) => (
                <option key={v.version} value={v.version}>
                  {v.version} ({v.responseCount} responses)
                </option>
              ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading comparison...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>Error: {error}</p>
        </div>
      ) : !comparisonData || !version1 || !version2 ? (
        <div className="cp-no-data">
          <p>Select two versions to compare their survey results.</p>
          {versionsForSurvey.length < 2 && (
            <p style={{ marginTop: 8, fontSize: '0.9rem' }}>
              This survey needs at least 2 versions with responses for comparison.
            </p>
          )}
        </div>
      ) : (
        <div className="cp-questions">
          {comparisonData.data.map((question) => {
            const delta = calculateDelta(question);
            const chartData = prepareChartData(question);

            return (
              <div key={question.questionId} className="cp-question-card">
                <div className="cp-question-header">
                  <h3 className="cp-question-text">
                    {getQuestionText(question.questionId)}
                  </h3>
                  {delta !== null && (
                    <span
                      className={`cp-delta-badge ${
                        delta > 5 ? 'positive' : delta < -5 ? 'negative' : 'neutral'
                      }`}
                    >
                      {delta > 0 ? '+' : ''}
                      {delta.toFixed(0)}% positive
                    </span>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={chartData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6d6d70' }} />
                    <YAxis
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 12, fill: '#6d6d70' }}
                      domain={[0, 100]}
                    />
                    <Tooltip
                      formatter={(value) => [`${value}%`, '']}
                      contentStyle={{
                        background: '#fff',
                        border: '1px solid #e5e5ea',
                        borderRadius: '8px',
                      }}
                    />
                    <Legend />
                    <Bar
                      dataKey={`v${version1}`}
                      name={version1}
                      fill={CHART_COLORS.primary}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey={`v${version2}`}
                      name={version2}
                      fill={CHART_COLORS.success}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            );
          })}
          {comparisonData.data.length === 0 && (
            <div className="cp-no-data">
              <p>No question data available for comparison.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComparisonPage;
